import {
  getSession,
  setActiveAuthProvider,
  setTokenToAxios,
  SESSION_STORAGE_KEY,
  USER_STORAGE_KEY,
  LC_SOURCE_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
} from '@onesource/services';
import { AuthProviderEnum, FEATURE_FLAGS_STORAGE_KEY, USE_SUPABASE_AUTH_FLAG_KEY } from '@onesource/schemas';
import { FC, ReactElement, useEffect, useState } from 'react';

export type TAuthProviderProps = {
  children: ReactElement;
};

// Run synchronously at module load — before axios interceptors or React can clear the session.
// If the stored JWT contains wp_token, clear the session and set USE_SUPABASE_AUTH flag,
// then hard reload so the app boots fresh with Supabase auth active.
(() => {
  try {
    const token = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!token) {
      return;
    }

    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!('wp_token' in payload)) {
      return;
    }

    console.log('[AuthProvider] WordPress token detected at module load, forcing logout');

    // Clear session data
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(LC_SOURCE_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);

    // Set USE_SUPABASE_AUTH to true in featureFlags, preserving other flags
    let flags: Record<string, unknown> = {};
    const raw = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null) {
        flags = parsed;
      }
    }
    flags[USE_SUPABASE_AUTH_FLAG_KEY] = true;
    localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(flags));

    console.log('[AuthProvider] Session cleared, USE_SUPABASE_AUTH set to true. Reloading...');
    window.location.reload();
  } catch {
    // Ignore errors — don't block app startup
  }
})();

export const AuthProvider: FC<TAuthProviderProps> = props => {
  const { children } = props;
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const initializeAuth = () => {
      // One-time migration: move legacy auth_provider key into featureFlags
      try {
        const legacyProvider = localStorage.getItem('auth_provider');
        if (legacyProvider) {
          if (legacyProvider === 'supabase') {
            setActiveAuthProvider(AuthProviderEnum.supabase);
          }
          localStorage.removeItem('auth_provider');
        }
      } catch {
        // Ignore migration errors
      }

      // Load token from localStorage and set it to axios
      const accessToken = getSession();
      if (accessToken) {
        setTokenToAxios(accessToken);
      }

      setIsReady(true);
    };

    initializeAuth();
  }, []);

  if (!isReady) {
    return null;
  }

  return children;
};
